import { Sms } from "@mui/icons-material"
import { Chip, Divider, Stack, Typography } from "@mui/material"
import moment from "moment"
import React, { FC } from "react"
import { useTranslation } from "react-i18next"
import {
  ReservationAud,
  ReservationAudDetailsSmsDeliveryUpdated,
} from "shared/reservationAud"
import { ReservationAudEventEnum } from "shared/reservationAudData"
import { TwilioStatusType } from "shared/smsDelivery"

type OsReservationAuditTrailDetailsSmsDeliveryUpdatedProps = {
  aud: ReservationAud
}

export const OsReservationAuditTrailDetailsSmsDeliveryUpdated: FC<
  OsReservationAuditTrailDetailsSmsDeliveryUpdatedProps
> = ({ aud }) => {
  const { t } = useTranslation()
  let details = aud.details as ReservationAudDetailsSmsDeliveryUpdated

  const [showPreview, setShowPreview] = React.useState(false)

  if (!details || typeof details !== "object") {
    return null
  }
  // event type must be ReservationAudEventEnum.smsDeliveryUpdated
  if (aud.event !== ReservationAudEventEnum.smsDeliveryUpdated) {
    return null
  }

  if (!details.smsDelivery || typeof details.smsDelivery !== "object") {
    return null
  }

  const getStatusColor = (status: TwilioStatusType) => {
    if (!status) return "black"
    switch (status.toLowerCase()) {
      case "delivered":
        return "green"
      case "sent":
        // return "#1976d2"
        return "grey"
      case "failed":
      case "undelivered":
        // error red
        return "#d32f2f"
      case "delivered_unkown":
        return "orange"
      case "accepted":
      case "scheduled":
      case "queued":
      case "sending":
        return "orange"
      default:
        return "orange"
    }
  }

  return (
    <Stack direction="column" spacing={1} p={1}>
      <Stack direction="column" spacing={1}>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Sms fontSize="small" color="primary" />
            <Typography variant="body2" color="primary">
              {t("smsDeliveryUpdated")}
            </Typography>
          </Stack>
          {/* <Sl2Btn
            size="small"
            variant="outlined"
            color="primary"
            onClick={() => setShowPreview(true)}
          >
            {t("preview")}
          </Sl2Btn> */}
        </Stack>
        <Divider />
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2" fontWeight="bold">
            {t("status")}:
          </Typography>
          <Chip
            label={details.smsDelivery.twilioStatus}
            size="small"
            sx={{
              backgroundColor: getStatusColor(details.smsDelivery.twilioStatus),
              color: "white",
            }}
          />
        </Stack>
        {details.smsDelivery?.twilioErrorCode && (
          <Stack direction="column" spacing={1}>
            <Stack direction="row" spacing={1}>
              <Typography variant="body2" color="error">
                {t(
                  `twilioError${details.smsDelivery?.twilioErrorCode}`,
                  t("twilioErrorGeneric") +
                    ` ${details.smsDelivery?.twilioErrorCode}`,
                )}
              </Typography>
            </Stack>
          </Stack>
        )}
        {/*
          Never render smsDelivery.deliveryError — the provider's raw string
          embeds the tenant's Twilio account SID (LOCKER-401). New records are
          redacted server-side and carry hasDeliveryError instead; the
          deliveryError check is kept only so audit records written before that
          redaction shipped still show the generic message.
          Suppressed when twilioErrorCode is set, since the block above already
          renders a translated explanation for that code.
        */}
        {(details.smsDelivery.hasDeliveryError ||
          details.smsDelivery.deliveryError) &&
          !details.smsDelivery.twilioErrorCode && (
            <Stack direction="row" spacing={1}>
              <Typography variant="body2" fontWeight="bold">
                {t("deliveryError")}:
              </Typography>
              <Typography variant="body2" color="error">
                {t("smsDeliveryFailedGeneric")}
              </Typography>
            </Stack>
          )}
        <Stack direction="row" spacing={1}>
          <Typography variant="body2" fontWeight="bold">
            {t("toNumber")}:
          </Typography>
          <Typography variant="body2">
            {details.smsDelivery.toPhoneNumber}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Typography variant="body2" fontWeight="bold">
            {t("fromNumber")}:
          </Typography>
          <Typography variant="body2">
            {details.smsDelivery.fromPhoneNumber}
          </Typography>
        </Stack>
        {/* {details.smsDelivery.sentAt && (
          <Stack direction="row" spacing={1}>
            <Typography variant="body2" fontWeight="bold">
              {t("sentAt")}:
            </Typography>
            <Typography variant="body2">
              {moment(details.smsDelivery.sentAt).format("YYYY-MM-DD HH:mm:ss")}
            </Typography>
          </Stack>
        )} */}
        {details.smsDelivery.deliveredAt && (
          <Stack direction="row" spacing={1}>
            <Typography variant="body2" fontWeight="bold">
              {t("deliveredAt")}:
            </Typography>
            <Typography variant="body2">
              {moment(details.smsDelivery.deliveredAt).format(
                "YYYY-MM-DD HH:mm:ss",
              )}
            </Typography>
          </Stack>
        )}
        {details.smsDelivery.retries !== undefined && (
          <Stack direction="row" spacing={1}>
            <Typography variant="body2" fontWeight="bold">
              {t("retries")}:
            </Typography>
            <Typography variant="body2">
              {details.smsDelivery.retries}
            </Typography>
          </Stack>
        )}
        {details.smsDelivery.retryTimestamps &&
          details.smsDelivery.retryTimestamps.length > 0 && (
            <Stack direction="column" spacing={1}>
              <Typography variant="body2" fontWeight="bold">
                {t("retryTimestamps")}:
              </Typography>
              {details.smsDelivery.retryTimestamps.map(
                (timestamp: number, index: number) => (
                  <Typography key={index} variant="body2">
                    {moment(timestamp).format("YYYY-MM-DD HH:mm:ss")}
                  </Typography>
                ),
              )}
            </Stack>
          )}
      </Stack>
      <Divider />
    </Stack>
  )
}
